import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Phone, Paperclip, Smile, MoreVertical, Search, User } from 'lucide-react';
import { GlassCard, GlassInput, GlassButton, FadeIn } from '../../components/ui';

const conversations = [
  { id: 1, name: 'Carlos Silva', avatar: 'CS', lastMessage: 'Olá, gostaria de agendar uma visita', time: '10:30', unread: 2, online: true },
  { id: 2, name: 'Maria Santos', avatar: 'MS', lastMessage: 'Obrigada pelo retorno!', time: '09:15', unread: 0, online: false },
  { id: 3, name: 'João Pereira', avatar: 'JP', lastMessage: 'Vamos fechar negócio?', time: 'Ontem', unread: 1, online: true },
  { id: 4, name: 'Ana Costa', avatar: 'AC', lastMessage: 'Pode me enviar mais fotos?', time: 'Ontem', unread: 0, online: false },
];

const messages = [
  { id: 1, sender: 'them', text: 'Olá, vi o anúncio do apartamento e gostaria de mais informações.', time: '10:25' },
  { id: 2, sender: 'me', text: 'Olá! Claro, fico feliz em ajudar. Qual apartamento especificamente?', time: '10:27' },
  { id: 3, sender: 'them', text: 'É o da Rua dos Pinheiros, número 123.', time: '10:28' },
  { id: 4, sender: 'me', text: 'Perfeito! Esse apartamento tem 3 quartos, 2 banheiros e uma área de 120m². O valor é R$ 850.000.', time: '10:29' },
  { id: 5, sender: 'them', text: 'Olá, gostaria de agendar uma visita', time: '10:30' },
];

const tabs = [
  { id: 'interno', label: 'Chat Interno' },
  { id: 'whatsapp', label: 'WhatsApp' },
];

export const Chat = () => {
  const [activeTab, setActiveTab] = useState('interno');
  const [activeConversation, setActiveConversation] = useState(conversations[0]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (newMessage.trim()) {
      setNewMessage('');
    }
  };

  return (
    <div className="space-y-6 h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Chat</h1>
          <p className="text-white/50">Comunicação interna e WhatsApp</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 text-white border border-white/10' 
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {tab.id === 'whatsapp' && <Phone className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Chat Container */}
      <FadeIn className="h-full">
        <GlassCard className="h-[calc(100%-120px)] flex overflow-hidden">
          {/* Conversations List */}
          <div className="w-80 border-r border-white/10 flex flex-col">
            <div className="p-4 border-b border-white/10">
              <GlassInput icon={Search} placeholder="Buscar conversa..." />
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setActiveConversation(conv)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-white/[0.03] transition-colors border-b border-white/5 ${
                    activeConversation?.id === conv.id ? 'bg-white/[0.05]' : ''
                  }`}
                >
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-primary/30 to-accent-secondary/30 flex items-center justify-center">
                      <span className="text-sm font-medium text-white/90">{conv.avatar}</span>
                    </div>
                    {conv.online && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-white/90 truncate">{conv.name}</span>
                      <span className="text-xs text-white/40">{conv.time}</span>
                    </div>
                    <p className="text-sm text-white/50 truncate">{conv.lastMessage}</p>
                  </div>
                  {conv.unread > 0 && (
                    <span className="w-5 h-5 rounded-full bg-accent-primary text-white text-xs flex items-center justify-center">
                      {conv.unread}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {/* Chat Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-primary/30 to-accent-secondary/30 flex items-center justify-center">
                  <span className="text-sm font-medium text-white/90">{activeConversation?.avatar}</span>
                </div>
                <div>
                  <p className="font-medium text-white/90">{activeConversation?.name}</p>
                  <p className="text-xs text-green-400">
                    {activeConversation?.online ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <GlassButton variant="ghost" size="icon">
                  <Phone className="w-5 h-5" />
                </GlassButton>
                <GlassButton variant="ghost" size="icon">
                  <MoreVertical className="w-5 h-5" />
                </GlassButton>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                      msg.sender === 'me'
                        ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-white'
                        : 'bg-white/10 text-white/90'
                    }`}
                  >
                    <p>{msg.text}</p>
                    <span className={`text-xs mt-1 block ${msg.sender === 'me' ? 'text-white/70' : 'text-white/40'}`}>
                      {msg.time}
                    </span>
                  </div>
                </motion.div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <GlassButton variant="ghost" size="icon">
                  <Paperclip className="w-5 h-5" />
                </GlassButton>
                <input
                  type="text"
                  placeholder="Digite sua mensagem..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white/90 placeholder:text-white/40 focus:outline-none focus:border-accent-primary/50"
                />
                <GlassButton variant="ghost" size="icon">
                  <Smile className="w-5 h-5" />
                </GlassButton>
                <GlassButton size="icon" onClick={handleSend}>
                  <Send className="w-5 h-5" />
                </GlassButton>
              </div>
            </div>
          </div>
        </GlassCard>
      </FadeIn>
    </div>
  );
};

export default Chat;
